/**
 * The External EMR keeps a claim only when it charts every day of it: Roger
 * charts from after the claim's last day, or the claim ended before today
 * (the External EMR charted all of it). Mirrors
 * `kept_claims_roger_charts` in the backend: the dialog and the census row
 * recompute it as the person changes the day or who bills each claim.
 */

import { formatDateForDisplay } from '../../utils/dateUtils';
import { chartsInsideKeptLine } from './takeOverCopy';

export type ClaimSpan = {
  number: number;
  start_date: string;
  end_date: string;
  /** Ended before today, or before the chart moved, in the agency's zone. */
  elapsed?: boolean;
};

/**
 * The kept claims Roger would chart days of from `chartsFrom`: the one the
 * day falls inside and every kept claim after it, ended claims left out.
 * ISO dates compare as text.
 */
export function keptClaimsRogerCharts<T extends ClaimSpan>(
  kept: readonly T[],
  chartsFrom: string | null | undefined,
  { includeEnded = false }: { includeEnded?: boolean } = {}
): T[] {
  if (!chartsFrom) return [];
  // After the move every claim counts: the change is a person's choice.
  return kept.filter(
    claim => (includeEnded || !claim.elapsed) && chartsFrom <= claim.end_date
  );
}

/**
 * The warning for the claims `keptClaimsRogerCharts` returned, shared by the
 * Take Over dialog and the census row. Null when there are none.
 */
export function keptClaimsWarning(
  chartsFrom: string,
  claims: readonly ClaimSpan[]
): string | null {
  if (claims.length === 0) return null;
  return chartsInsideKeptLine(
    formatDateForDisplay(chartsFrom),
    claims.map(claim => claim.number),
    formatDateForDisplay(claims[0].start_date),
    formatDateForDisplay(claims[claims.length - 1].end_date)
  );
}
